"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { Address } from "viem";
import { parseUnits } from "viem";
import { useAccount } from "wagmi";
import { z } from "zod";
import { Button, Field, Label } from "@/components/ui";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useDnmSourceAvailability } from "@/hooks/dao/voting/useDnmSourceAvailability";
import { useVoteSubmit } from "@/hooks/dao/voting/useVoteSubmit";
import type { ValidationT } from "@/lib/validations/i18n";
import { AmountSection } from "./AmountSection";
import { SourcePicker } from "./SourcePicker";

const SLOW_SUBMIT_DELAY_MS = 15_000;

/**
 * Vote drawer form — Figma 5462:52047 / 52187 (Phase 2).
 *
 * Renders source picker + amount input + submit.
 * Uses react-hook-form + zod. Validation errors shown inline via RHFInput.
 *
 * AC-05..AC-19, AC-34, EC-02, EC-10, EC-11, EC-14, EC-15.
 */

// Minimum vote threshold: Election.sol isValidValue requires > 0.001 ether
const MIN_DNM_WEI = parseUnits("0.001", 18);

/**
 * R6 schema factory — module-scope schemas can't call `useTranslations`, so the
 * component builds this via `useMemo(() => makeVoteFormSchema(vt), [vt])` with
 * `const vt = useTranslations("validation")`.
 */
function makeVoteFormSchema(vt: ValidationT) {
  return z.object({
    source: z.enum(["wallet", "stake"]).optional(),
    amount: z
      .string()
      .min(1, { message: vt("dnmAmountPositive") })
      .refine((v) => /^\d+(\.\d{1,18})?$/.test(v), {
        message: vt("dnmAmountInvalid"),
      })
      .refine(
        (v) => {
          try {
            return parseUnits(v, 18) > 0n;
          } catch {
            return false;
          }
        },
        { message: vt("dnmAmountPositive") },
      )
      .refine(
        (v) => {
          try {
            return parseUnits(v, 18) >= MIN_DNM_WEI;
          } catch {
            return false;
          }
        },
        { message: vt("dnmAmountMin") },
      ),
  });
}

type VoteFormValues = z.infer<ReturnType<typeof makeVoteFormSchema>>;

interface VoteDrawerFormProps {
  candidate: { address: Address; nickname: string };
  onSuccess: () => void;
}

export const VoteDrawerForm: React.FC<VoteDrawerFormProps> = ({
  candidate,
  onSuccess,
}) => {
  const t = useTranslations("dao");
  const vt = useTranslations("validation");
  const { address: userAddress } = useAccount();
  const { cycle } = useCurrentCycle();
  const availability = useDnmSourceAvailability(userAddress, cycle);
  const submit = useVoteSubmit({
    candidateAddress: candidate.address,
  });

  const voteFormSchema = useMemo(() => makeVoteFormSchema(vt), [vt]);

  const form = useForm<VoteFormValues>({
    resolver: zodResolver(voteFormSchema),
    defaultValues: { source: undefined, amount: "" },
    mode: "onChange",
  });

  const source = form.watch("source");
  const amountStr = form.watch("amount");

  // Compute max for current source
  const max =
    source === "wallet"
      ? availability.walletDnm
      : source === "stake"
        ? availability.availableStaked
        : 0n;

  // Compute whether amount exceeds the max (inline validation beyond zod)
  let amountWei = 0n;
  let amountExceedsMax = false;
  if (source !== undefined && amountStr) {
    try {
      amountWei = parseUnits(amountStr, 18);
      amountExceedsMax = amountWei > max;
    } catch {
      amountExceedsMax = false;
    }
  }

  const isSubmitEnabled =
    source !== undefined &&
    amountWei > 0n &&
    !amountExceedsMax &&
    !availability.isError &&
    !submit.isPending &&
    form.formState.isValid;

  // "this is taking long" affordance — same 15s pending pattern as the
  // candidacy form and the checkout flow. Future: extract to a shared
  // submit-with-hint primitive once we wire it into every chain-touching
  // submit (per PM directive 2026-05-06).
  const [showSlowHint, setShowSlowHint] = useState(false);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (submit.isPending) {
      slowTimerRef.current = setTimeout(() => {
        setShowSlowHint(true);
      }, SLOW_SUBMIT_DELAY_MS);
    } else {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      setShowSlowHint(false);
    }
    return () => {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, [submit.isPending]);

  const onSubmit = async (values: VoteFormValues) => {
    if (!values.source) return;
    try {
      const wei = parseUnits(values.amount, 18);
      await submit.mutateAsync({
        source: values.source,
        amount: wei,
      });
      onSuccess();
    } catch {
      // errors handled inside useVoteSubmit via toast
    }
  };

  // Additional cross-field validation: amount > max
  const amountError = amountExceedsMax
    ? source === "wallet"
      ? t("voting.amount.exceedsWallet")
      : t("voting.amount.exceedsStaked")
    : undefined;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        {/* Source picker label — Figma 5462:52047 */}
        <Field>
          <Label>{t("voting.source.question")}</Label>
        </Field>

        <SourcePicker
          walletBalance={availability.walletDnm}
          stakedAvailable={availability.availableStaked}
          selected={source}
          onSelect={(s) => form.setValue("source", s, { shouldValidate: true })}
          isError={availability.isError}
        />

        {/* Amount section — Figma 5462:52061 */}
        <AmountSection
          maxAmount={max}
          sourceSelected={source !== undefined}
          isError={availability.isError}
        />

        {/* Cross-field error: amount > max. Always rendered so the layout
            slot stays reserved — toggling the message in/out would otherwise
            shift the submit button by ~16px. text-start aligns with the
            input column above instead of inheriting parent center alignment. */}
        <p className="min-h-4 text-xs text-start text-semantic-error">
          {amountError ?? "\u00A0"}
        </p>

        {/* Submit — Figma 5462:52047 disabled / 5462:52187 enabled */}
        <Button
          type="submit"
          variant="solid"
          colors="primary"
          size="md"
          radius="xs"
          className="self-end w-24"
          disabled={!isSubmitEnabled}
          loading={submit.isPending}
        >
          {t("voting.button.submit")}
        </Button>

        {showSlowHint && (
          <div
            className="flex items-center justify-center gap-2 rounded-md border border-border bg-bg-secondary px-3 py-2 text-xs text-text-secondary"
            role="status"
            aria-live="polite"
          >
            <span
              className="size-3 shrink-0 animate-spin rounded-full border-2 border-border border-t-text-secondary"
              aria-hidden
            />
            <span>{t("voting.form.slowHint")}</span>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
